import * as React from 'react'

import useFocus from '../../../Shared/hooks/useFocus'
import { READ_PASSAGE_STEP_NUMBER } from '../../../Shared/index'
import { BECAUSE, BUT, SO } from '../../../../constants/evidence'
import { getActivityStepLabels, orderedSteps } from '../../helpers/containerActionHelpers'

const stepCopyByConjunction = {
  [BECAUSE]: <p>Expand a sentence using <u>because</u> to provide a reason.</p>,
  [BUT]: <p>Expand a sentence using <u>but</u> to provide an opposing idea.</p>,
  [SO]: <p>Expand a sentence using <u>so</u> to provide a result.</p>,
}

const READ_PASSAGE_HTML = <p>Read a text and highlight sentences.</p>

const completedCheckSrc = `${window.postbuildEnv.CDN_URL}/images/icons/xs/check.svg`

const COUNT_WORDS = ['zero', 'one', 'two', 'three', 'four']

interface StepProps {
  step: {
    html: React.ReactElement,
    stepNumber: number
  },
  title: string,
  active?: boolean,
  completed?: boolean,
  handleClick?: () => void
}

const Step = ({ active, completed, handleClick, step, title }: StepProps) => {
  const { html, stepNumber } = step;

  const contents = (
    <React.Fragment>
      <div className="step-overview-step-number">
        {completed ? <img alt="" src={completedCheckSrc} /> : stepNumber}
      </div>
      <div className="step-overview-step-text">
        {active && <span className="sr-only">This is the active step:</span>}
        {completed && <span className="sr-only">Completed step:</span>}
        <p className="step-overview-step-title">{title}</p>
        {html}
      </div>
      {active && <span aria-hidden="true" className="now-tag">Now</span>}
    </React.Fragment>
  )

  if (active) {
    return (
      <section className="step-overview-step-container">
        <button className="step-overview-step active focus-on-dark" onClick={handleClick} type="button">
          {contents}
        </button>
      </section>
    )
  }

  return (
    <section className="step-overview-step-container">
      <div className={`step-overview-step ${completed ? 'completed' : ''}`}>
        {contents}
      </div>
    </section>
  )
}

const StepOverview = ({ activeStep, activities, handleClick, }) => {
  const [containerRef, setContainerFocus] = useFocus()

  React.useEffect(() => {
    setContainerFocus()
  }, [activeStep])

  const orderedPrompts = orderedSteps(activities)
  const sentenceCount = orderedPrompts.length
  const sentences = `${COUNT_WORDS[sentenceCount] || sentenceCount} ${sentenceCount === 1 ? 'sentence' : 'sentences'}`
  // Pairs with getActivitySteps, so index 0 is the read step and the rest follow the prompts.
  const stepLabels = getActivityStepLabels(activities)
  const promptSteps = orderedPrompts.map((prompt, i) => ({
    stepNumber: i + 2,
    html: stepCopyByConjunction[prompt.conjunction],
    title: stepLabels[i + 1],
  }))

  if (activeStep === READ_PASSAGE_STEP_NUMBER) {
    return (
      <div className="step-overview">
        <h1 className="no-focus-outline" ref={containerRef} tabIndex={-1}>Here’s what you’ll do</h1>
        <p className="step-overview-subheader">{`You’ll read a text, write ${sentences}, and revise each sentence up to four times.`}</p>
        <Step
          active={true}
          handleClick={handleClick}
          step={{ html: READ_PASSAGE_HTML, stepNumber: READ_PASSAGE_STEP_NUMBER }}
          title={stepLabels[0]}
        />
        {promptSteps.map(step => (
          <Step
            active={activeStep === step.stepNumber}
            key={step.stepNumber}
            step={step}
            title={step.title}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="step-overview">
      <h1 ref={containerRef} tabIndex={-1}>Nice! Keep going!</h1>
      <Step
        active={false}
        completed={activeStep > READ_PASSAGE_STEP_NUMBER}
        handleClick={handleClick}
        step={{ html: READ_PASSAGE_HTML, stepNumber: READ_PASSAGE_STEP_NUMBER }}
        title={stepLabels[0]}
      />
      {promptSteps.map(step => (
        <Step
          active={activeStep === step.stepNumber}
          completed={activeStep > step.stepNumber}
          handleClick={handleClick}
          key={step.stepNumber}
          step={step}
          title={step.title}
        />
      ))}
    </div>
  )
}

export default StepOverview
